/**
 * Real NFL data provider.
 *
 * Source: ESPN's public NFL endpoints. No API key and no account.
 *
 * WHICH HOST, AND WHY IT MATTERS
 * ------------------------------
 * ESPN exposes the same data on several hosts, and they do not behave the
 * same way from a browser:
 *
 *   site.api.espn.com   works from a server, but its Akamai edge returns
 *                       403 to browser-shaped requests (verified: identical
 *                       curl succeeds without browser headers and fails
 *                       with Origin/Sec-Fetch-* set). Unusable here.
 *   cdn.espn.com        the host espn.com's own web app calls. Serves CORS
 *                       correctly and works directly from the browser.
 *
 * So this provider uses `cdn.espn.com/core/nfl/*?xhr=1`, which is what the
 * ESPN website itself fetches. Same data, same shapes: the payload nests
 * it under `gamepackageJSON` rather than returning it at the top level.
 *
 * LICENSING: read before shipping commercially. These endpoints are
 * undocumented and carry no published terms of use. Fine for a private
 * beta; a commercial launch needs a licensed feed. Recorded, with the
 * alternatives, in docs/sports-data/README.md.
 */

import { fetchJson } from "../../utils/httpClient";
import {
  normalizeDrives,
  normalizeGame,
  normalizeScheduleDays,
  normalizeState,
  toEspnTeamAbbreviation,
} from "./espnNormalizer";
import {
  normalizeGameRecap,
  normalizeSeasonBracket,
  normalizeSeasonStandings,
  normalizeTeamSchedule,
} from "./seasonContextNormalizer";
import { buildPlayerIndex } from "./playerNameIndex";
import { normalizeSeasonCalendar, seasonSpanOf } from "./espnSeasonProvider";
import { getTeam } from "../../data/teams";
import { GAME_STATUS } from "../../utils/gameEngine";

const BASE_URL =
  String(process.env.REACT_APP_SPORTS_API_BASE_URL ?? "").trim() ||
  "https://cdn.espn.com/core/nfl";

const TEAM_API_BASE_URL =
  String(process.env.REACT_APP_TEAM_API_BASE_URL ?? "").trim() ||
  "https://site.web.api.espn.com";

/**
 * HOW LONG AN ANSWER IS WORTH TRUSTING, AND HOW OFTEN TO ASK AGAIN
 * ----------------------------------------------------------------
 * These two things are one decision, so they live together. A caller that
 * polls faster than the cache window cannot learn anything: it gets the
 * same cached object back and then tells the viewer the data is fresh.
 * That is exactly how a five-minute-old score ended up under a pulsing
 * "Live" pip, so every interval below is chosen against a window.
 */

/** Games days away do not change. Nothing is lost by asking rarely. */
const SCHEDULE_TTL_MS = 5 * 60_000;

/** A schedule with a game running in it changes with every score. */
const LIVE_SCHEDULE_TTL_MS = 30_000;

/**
 * A kickoff this close is worth watching for. Deliberately the same as
 * the idle window: a kickoff can then never hide between two idle polls,
 * because every idle poll looks a whole idle window ahead.
 */
const KICKOFF_WATCH_MS = SCHEDULE_TTL_MS;

/**
 * Past this, a row still marked "scheduled" is a stale row rather than an
 * imminent kickoff, and hammering the feed will not fix it.
 */
const KICKOFF_GRACE_MS = 4 * 60 * 60_000;

const LIVE_TTL_MS = 10_000;
const STANDINGS_TTL_MS = 5 * 60_000;
const TEAM_SCHEDULE_TTL_MS = 30 * 60_000;

/**
 * How often it is worth asking about a game that is under way.
 *
 * Longer than LIVE_TTL_MS on purpose: a poll inside that window is served
 * from cache, so it can neither surface a new play nor honestly refresh
 * "last updated". Still far shorter than the gap between plays, which is
 * tens of seconds once the huddle and play clock are counted, so a new
 * play reaches the screen within one interval. Every poll costs the
 * viewer battery and costs ESPN a request, so this is the slowest rate
 * that still feels live rather than the fastest we could manage.
 */
export const LIVE_POLL_MS = 15_000;

/**
 * How often the schedule is worth re-asking, given what is on it.
 *
 * Exported so the hook that polls and the cache that answers cannot drift
 * apart: this is the single definition of both the cache window and the
 * poll interval. Simulated games are ignored because they are computed
 * locally and never go stale.
 *
 * @param {Array<{status?: string, kickoffAt?: number, isSimulated?: boolean}>} games
 * @param {{now?: number}} options
 * @returns {number} milliseconds
 */
export function scheduleRefreshMs(games = [], { now = Date.now() } = {}) {
  const real = (Array.isArray(games) ? games : []).filter(
    (game) => game && !game.isSimulated
  );

  const running = real.some((game) => game.status === GAME_STATUS.IN_PROGRESS);
  if (running) return LIVE_SCHEDULE_TTL_MS;

  const aboutToStart = real.some((game) => {
    if (game.status !== GAME_STATUS.SCHEDULED || game.kickoffAt == null) {
      return false;
    }
    const untilKickoff = game.kickoffAt - now;
    return untilKickoff <= KICKOFF_WATCH_MS && untilKickoff >= -KICKOFF_GRACE_MS;
  });

  return aboutToStart ? LIVE_SCHEDULE_TTL_MS : SCHEDULE_TTL_MS;
}

const cache = new Map();

/** A ttl may be a fixed number or a function of the cached payload. */
const ttlFor = (ttl, value, now) =>
  typeof ttl === "function" ? ttl(value, now) : ttl;

async function cachedFetch(url, ttl, { now = Date.now() } = {}) {
  const hit = cache.get(url);
  if (hit && now - hit.at < ttlFor(ttl, hit.value, now)) return hit.value;

  const value = await fetchJson(url, { retries: 1, timeoutMs: 8000 });
  cache.set(url, { at: now, value });
  return value;
}

/** When the copy of `url` we would serve was actually fetched, if ever. */
const cachedAt = (url) => cache.get(url)?.at ?? null;

/**
 * Freshness rides alongside the list rather than inside it: every element
 * of the list is a game, and the caller needs to know when this data left
 * the network, not when it asked. Non-enumerable so nothing that walks or
 * serialises the list trips over it.
 *
 * @param {Array} games
 * @param {number|null} fetchedAt epoch ms, or null if we never got it
 */
export function withFetchedAt(games, fetchedAt) {
  return Object.defineProperty(games, "fetchedAt", {
    value: fetchedAt,
    enumerable: false,
    configurable: true,
    writable: true,
  });
}

/** The oldest real fetch behind a set of responses, which is the honest one. */
function oldestFetch(urls) {
  const stamps = urls.map(cachedAt).filter((at) => at != null);
  return stamps.length > 0 ? Math.min(...stamps) : null;
}

const scheduleUrl = (params = "") =>
  `${BASE_URL}/schedule?xhr=1${params}`;

const standingsUrl = ({ year } = {}) =>
  `${BASE_URL}/standings?xhr=1&group=conference${
    year == null ? "" : `&season=${encodeURIComponent(year)}`
  }`;

const teamScheduleUrl = (teamId, { year, seasonType }) =>
  `${TEAM_API_BASE_URL}/apis/site/v2/sports/football/nfl/teams/${encodeURIComponent(
    teamId
  )}/schedule?season=${encodeURIComponent(year)}&seasontype=${encodeURIComponent(
    seasonType
  )}`;

/** The cache window for a schedule payload, decided by what is on it. */
const scheduleTtl = (payload, now) =>
  scheduleRefreshMs(normalizeScheduleDays(payload), { now });

/**
 * One week of one season, as ESPN's own URL for it.
 *
 * The parameterless form (every argument null) is the current week, which
 * is what the live schedule asks for and must keep asking for: it is the
 * only request whose answer follows the league forward on its own.
 */
export function seasonWeekParams({ year, seasonType, week } = {}) {
  if (year == null) return "";
  return `&year=${year}&seasontype=${seasonType}&week=${week}`;
}

/**
 * The raw schedule payload for one week, through the by-URL cache.
 *
 * Exported because two callers want the same response for two different
 * reasons: the fixtures on it, and the `calendar` beside them that says how
 * that season's weeks are arranged. Sharing one cached fetch means asking
 * both questions about the week already on screen costs one request rather
 * than two.
 */
export async function fetchSchedulePayload(params = {}, options = {}) {
  const url = scheduleUrl(seasonWeekParams(params));
  const payload = await cachedFetch(url, scheduleTtl, options);
  return { payload, url, fetchedAt: cachedAt(url) };
}

/**
 * The schedule around now.
 *
 * The parameterless call returns the current week. We read the season and
 * week out of that response and pull the next two weeks as well, so a
 * beginner opening the app mid-week still has something to browse.
 *
 * The returned list carries `fetchedAt`: the oldest of the responses it
 * was built from, so a caller can say how old the data is without
 * guessing (see withFetchedAt).
 */
async function getUpcomingGames({ limit = 20, now = Date.now() } = {}) {
  const currentUrl = scheduleUrl();
  const urls = [currentUrl];

  const current = await cachedFetch(currentUrl, scheduleTtl, { now });
  const games = [...normalizeScheduleDays(current)];

  // Where are we in the season? Taken from the data, never assumed.
  const sample = games[0];
  if (sample?.season && sample?.week != null) {
    const { season, week } = sample;
    const seasonType = sample.seasonTypeId ?? 1;

    const extra = await Promise.all(
      [week + 1, week + 2].map((next) => {
        const url = scheduleUrl(
          `&year=${season}&seasontype=${seasonType}&week=${next}`
        );
        urls.push(url);

        return cachedFetch(url, scheduleTtl, { now })
          .then(normalizeScheduleDays)
          .catch(() => []);
      })
    );
    extra.forEach((list) => games.push(...list));
  }

  // Deduplicate, then live games first, upcoming next, finals last.
  const seen = new Set();
  const unique = games.filter((game) =>
    seen.has(game.gameId) ? false : seen.add(game.gameId)
  );

  const rank = (game) =>
    game.status === GAME_STATUS.IN_PROGRESS
      ? 0
      : game.status === GAME_STATUS.SCHEDULED
        ? 1
        : 2;

  const ordered = unique
    .sort((a, b) => rank(a) - rank(b) || (a.kickoffAt ?? 0) - (b.kickoffAt ?? 0))
    .slice(0, limit);

  return withFetchedAt(ordered, oldestFetch(urls));
}

/**
 * One named week of one named season.
 *
 * The counterpart to `getUpcomingGames`, and deliberately not a variant of
 * it. "Upcoming" is a question about now: it reads the current week out of
 * the response and pulls the two after it, because somebody opening the app
 * on a Wednesday wants Sunday's fixtures. Asked of a season that finished
 * ten years ago the question means nothing, so this one does not ask it.
 * It fetches exactly the week it was given, in kickoff order, which is the
 * order that week was played in.
 *
 * The list carries `fetchedAt`, same as the live one, so a caller can say
 * how old the data is without guessing.
 *
 * @param {object} args
 * @param {number} args.year
 * @param {number} args.seasonType 1 preseason, 2 regular, 3 postseason
 * @param {number} args.week
 * @param {number} [args.limit]
 * @param {number} [args.now] epoch ms, for tests
 */
async function getSeasonGames({ year, seasonType, week, limit = 20, now = Date.now() } = {}) {
  const { payload, fetchedAt } = await fetchSchedulePayload(
    { year, seasonType, week },
    { now }
  );

  const games = normalizeScheduleDays(payload);
  const ordered = [...games]
    .sort((a, b) => (a.kickoffAt ?? 0) - (b.kickoffAt ?? 0))
    .slice(0, limit);

  const answer = withFetchedAt(ordered, fetchedAt);

  return Object.defineProperty(answer, "unmapped", {
    value: Math.max(0, listedGameCount(payload) - games.length),
    enumerable: false,
    configurable: true,
    writable: true,
  });
}

/**
 * How many games ESPN filed under this week, before anything was dropped.
 *
 * `normalizeGame` returns null for a game it cannot map both clubs of, and
 * in an old season that is not a rare error: clubs move. St Louis, San
 * Diego and Oakland are all in the 2015 fixture list and none of them
 * exists under those names today, so three of that week's sixteen games
 * quietly disappear. Counting what was listed is what lets the page say
 * so instead of pretending the week was short.
 *
 * Deliberately not "fixed" by mapping St Louis onto the Los Angeles Rams:
 * that would put a badge and a city on a 2015 game that were not there,
 * which is a worse lie than an honest gap.
 */
function listedGameCount(payload) {
  return Object.values(payload?.content?.schedule ?? {}).reduce(
    (total, day) => total + (day?.games?.length ?? 0),
    0
  );
}

/**
 * How that season's weeks are arranged, from the same response.
 *
 * `content.calendar` describes the whole season whichever week you ask at,
 * so this is asked at the week already being viewed and is normally served
 * out of the cache the fixtures were just read from.
 */
async function getSeasonWeeks({ year, seasonType, week, now = Date.now() } = {}) {
  const { payload } = await fetchSchedulePayload({ year, seasonType, week }, { now });
  const phases = normalizeSeasonCalendar(payload?.content?.calendar);

  if (phases.length === 0) {
    throw new Error(`ESPN returned no week structure for the ${year} season.`);
  }

  return { year: Number(year), phases, span: seasonSpanOf(phases) };
}

async function getSeasonStandings({ year, now = Date.now() } = {}) {
  const url = standingsUrl({ year });
  const payload = await cachedFetch(url, STANDINGS_TTL_MS, { now });
  return {
    ...normalizeSeasonStandings(payload, { year }),
    fetchedAt: cachedAt(url),
  };
}

async function getSeasonBracket({ year, now = Date.now() } = {}) {
  const selectedYear = Number(year);
  if (!Number.isInteger(selectedYear)) {
    return { year: null, rounds: [], source: "espn", fetchedAt: null };
  }

  let weeks;
  try {
    weeks = await getSeasonWeeks({
      year: selectedYear,
      seasonType: 3,
      week: 1,
      now,
    });
  } catch {
    return {
      year: selectedYear,
      rounds: [],
      source: "espn",
      fetchedAt: null,
      unavailable: true,
    };
  }

  const postseason = weeks.phases.find((phase) => phase.seasonTypeId === 3);
  const roundWeeks = postseason?.weeks ?? [];

  const loaded = await Promise.all(
    roundWeeks.map(async (round) => {
      try {
        const { payload, fetchedAt } = await fetchSchedulePayload(
          { year: selectedYear, seasonType: 3, week: round.number },
          { now }
        );
        return {
          label: round.label,
          week: round.number,
          payload,
          fetchedAt,
        };
      } catch {
        return {
          label: round.label,
          week: round.number,
          payload: null,
          fetchedAt: null,
        };
      }
    })
  );

  const bracket = normalizeSeasonBracket(loaded, { year: selectedYear });
  const stamps = loaded
    .map((round) => round.fetchedAt)
    .filter((stamp) => Number.isFinite(stamp));

  return {
    ...bracket,
    fetchedAt: stamps.length > 0 ? Math.min(...stamps) : null,
  };
}

async function getTeamGames(teamId, { year, now = Date.now() } = {}) {
  const espnTeamId = toEspnTeamAbbreviation(teamId);
  const selectedYear = Number(year);
  if (!espnTeamId || !Number.isInteger(selectedYear)) {
    return Object.defineProperty([], "fetchedAt", {
      value: null,
      enumerable: false,
      configurable: true,
      writable: true,
    });
  }

  const loaded = await Promise.all(
    [1, 2, 3].map(async (seasonType) => {
      const url = teamScheduleUrl(espnTeamId, {
        year: selectedYear,
        seasonType,
      });

      try {
        const payload = await cachedFetch(url, TEAM_SCHEDULE_TTL_MS, { now });
        return {
          seasonType,
          games: normalizeTeamSchedule(payload, {
            teamId,
            seasonType,
          }),
          fetchedAt: cachedAt(url),
        };
      } catch {
        return { seasonType, games: [], fetchedAt: null };
      }
    })
  );

  const games = loaded
    .flatMap((entry) => entry.games)
    .sort((a, b) => (a.kickoffAt ?? 0) - (b.kickoffAt ?? 0));
  const stamps = loaded
    .map((entry) => entry.fetchedAt)
    .filter((stamp) => Number.isFinite(stamp));

  return Object.defineProperties(games, {
    fetchedAt: {
      value: stamps.length > 0 ? Math.min(...stamps) : null,
      enumerable: false,
      configurable: true,
      writable: true,
    },
    source: {
      value: "espn",
      enumerable: false,
      configurable: true,
      writable: true,
    },
  });
}

const providerIdOf = (gameId) => String(gameId ?? "").replace(/^nfl-/, "");

/** The game package: header, drives, everything about one game. */
async function fetchGamePackage(gameId, options = {}) {
  const url = `${BASE_URL}/game?xhr=1&gameId=${encodeURIComponent(providerIdOf(gameId))}`;
  const payload = await cachedFetch(url, LIVE_TTL_MS, options);
  return payload?.gamepackageJSON ?? null;
}

/**
 * ESPN's game package nests the event under `header`, in the same shape
 * the schedule uses, so one normalizer covers both.
 */
function eventFromPackage(pkg) {
  const header = pkg?.header;
  const competition = header?.competitions?.[0];
  if (!competition) return null;

  return {
    id: header.id,
    date: competition.date ?? header.competitions?.[0]?.date,
    season: header.season,
    week: { number: header.week },
    competitions: [competition],
  };
}

async function getGame(gameId, options = {}) {
  const pkg = await fetchGamePackage(gameId, options);
  const event = eventFromPackage(pkg);
  return event ? normalizeGame(event) : null;
}

async function getLiveGame(gameId, options = {}) {
  const pkg = await fetchGamePackage(gameId, options);
  const event = eventFromPackage(pkg);
  return event ? normalizeState(event, { at: options.at ?? Date.now() }) : null;
}

async function getGameEvents(gameId, options = {}) {
  const pkg = await fetchGamePackage(gameId, options);
  return normalizeDrives(pkg);
}

/**
 * State and events from one request.
 *
 * The snapshot also carries `playerIndex`: the athletes in this game's own
 * boxscore, keyed the way play-by-play text spells them, so the play feed
 * can put a face beside "J.Williams" (see playerNameIndex). It is built
 * from the package already fetched above, so it costs no extra request.
 *
 * It is deliberately allowed to be empty. `buildPlayerIndex` returns a
 * usable empty index for a package with no `boxscore.players` in it, and
 * a real pre-kickoff package has exactly that shape: verified against
 * cdn.espn.com, and captured as `__fixtures__/espn-pregame-401873272.json`.
 * An empty index resolves nothing, so the feed renders plain text and no
 * caller needs a special case. Whether an in-progress package carries the
 * block is UNVERIFIED: no NFL game was live to check against. If it does
 * not, this feature silently shows nothing, which is the intended failure.
 */
async function getLiveSnapshot(gameId, context = {}) {
  const at = context.at ?? Date.now();
  const pkg = await fetchGamePackage(gameId, context);

  const event = eventFromPackage(pkg);
  if (!event) {
    return { game: null, state: null, events: [], playCount: 0, totalPlays: 0 };
  }

  const game = normalizeGame(event);
  const state = normalizeState(event, { at });
  const events = normalizeDrives(pkg);
  const latestEvent = events[events.length - 1] ?? null;

  return {
    game,
    state: {
      ...state,
      latestEvent,
      // Ball position is only reliable from the play feed; see the note in
      // espnNormalizer.normalizeState.
      ballPosition: latestEvent?.resultingBallPosition ?? state.ballPosition,
    },
    events,
    recap: normalizeGameRecap(pkg, { gameId }),
    playCount: events.length,
    totalPlays: events.length,
    playerIndex: buildPlayerIndex(pkg),
  };
}

async function getGameRecap(gameId, options = {}) {
  const pkg = await fetchGamePackage(gameId, options);
  return normalizeGameRecap(pkg, { gameId });
}

async function getTeamById(teamId) {
  return getTeam(teamId);
}

/** No licensed roster source, so we never name a player. */
async function getPlayer() {
  return null;
}

export function __clearCacheForTests() {
  cache.clear();
}

const espnProvider = Object.freeze({
  id: "nfl",
  isSimulated: false,
  label: "Live NFL data (ESPN public endpoints)",
  attribution: "NFL schedule and scores from ESPN's public data.",

  getUpcomingGames,
  getSeasonGames,
  getSeasonWeeks,
  getSeasonStandings,
  getSeasonBracket,
  getTeamGames,
  getGame,
  getLiveGame,
  getGameEvents,
  getGameRecap,
  getLiveSnapshot,
  getTeam: getTeamById,
  getPlayer,
});

export default espnProvider;
